import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../lib/auth";
import { formatDate } from "../../lib/format";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { useUsers, toUserMap } from "../users/api";
import { canManageMasterData } from "./permissions";
import {
  useAdminAreas,
  useAdminCategories,
  useAdminSeverities,
  useAdminTags,
  type AdminArea,
  type AdminCategory,
  type AdminSeverity,
  type AdminTag,
} from "./api";
import {
  buildAreaBody,
  buildCategoryBody,
  buildSeverityBody,
  buildTagBody,
} from "./helpers";
import {
  MasterDataSection,
  type Column,
  type FieldSpec,
} from "./master-data-section";
import { StaleTagsPanel } from "./stale-tags-panel";

/** null/undefined -> "" so a row prefills the string-only dialog form. */
function s(value: string | number | null | undefined): string {
  return value == null ? "" : String(value);
}

const DASH = "-";

/**
 * F-009 Stammdatenverwaltung - the master-data half of the Administrationsbereich
 * (nur Admin, SRS sec 2071), sitting alongside Benutzer & Rollen. Four kinds in
 * tabs (Kategorien, Bereiche, Schweregrade, Tags); each is a full management
 * table with create / edit / deactivate / reactivate. Deactivation (not
 * deletion) keeps historical incidents resolvable (DO-007-style soft delete),
 * and the new-incident picker offers only active entries (AC-010). Display
 * gating only - the server re-checks every /admin route (ADR-API-09).
 */
export function AdminMasterDataPage(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();

  const categoriesQuery = useAdminCategories();
  const areasQuery = useAdminAreas();
  const severitiesQuery = useAdminSeverities();
  const tagsQuery = useAdminTags();
  const usersQuery = useUsers();

  // Parent-category and responsible-user lookups for columns + select options.
  const categoryById = useMemo(
    () => new Map((categoriesQuery.data ?? []).map((c) => [c.id, c])),
    [categoriesQuery.data],
  );
  const userMap = useMemo(() => toUserMap(usersQuery.data), [usersQuery.data]);

  const categoryOptions = useMemo(
    () =>
      (categoriesQuery.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    [categoriesQuery.data],
  );
  const userOptions = useMemo(
    () =>
      (usersQuery.data ?? []).map((u) => ({ value: u.id, label: u.displayName })),
    [usersQuery.data],
  );

  if (!canManageMasterData(user)) {
    return (
      <div className="rounded-lg border border-dashed px-6 py-16 text-center text-muted-foreground">
        {t("errors.forbidden")}
      </div>
    );
  }

  /* ----- Kategorien ----- */
  const categoryColumns: Column<AdminCategory>[] = [
    {
      key: "name",
      headerKey: "adminMasterData.colName",
      render: (r) => r.name,
      sortValue: (r) => r.name,
    },
    {
      key: "description",
      headerKey: "adminMasterData.colDescription",
      render: (r) => r.description ?? DASH,
      className: "text-muted-foreground",
      sortValue: (r) => r.description,
    },
    {
      key: "rank",
      headerKey: "adminMasterData.colRank",
      render: (r) => r.rank,
      sortValue: (r) => r.rank,
    },
    {
      key: "symbolHint",
      headerKey: "adminMasterData.colSymbol",
      render: (r) => r.symbolHint ?? DASH,
    },
    {
      key: "parentId",
      headerKey: "adminMasterData.colParent",
      render: (r) => (r.parentId ? categoryById.get(r.parentId)?.name ?? DASH : DASH),
      sortValue: (r) => (r.parentId ? categoryById.get(r.parentId)?.name ?? null : null),
    },
  ];
  const categoryFields: FieldSpec[] = [
    { name: "name", labelKey: "adminMasterData.colName", type: "text" },
    {
      name: "description",
      labelKey: "adminMasterData.colDescription",
      type: "textarea",
    },
    { name: "rank", labelKey: "adminMasterData.colRank", type: "number" },
    { name: "symbolHint", labelKey: "adminMasterData.colSymbol", type: "text" },
    {
      name: "parentId",
      labelKey: "adminMasterData.colParent",
      type: "select",
      options: categoryOptions,
    },
  ];

  /* ----- Bereiche ----- */
  const areaColumns: Column<AdminArea>[] = [
    {
      key: "name",
      headerKey: "adminMasterData.colName",
      render: (r) => r.name,
      sortValue: (r) => r.name,
    },
    {
      key: "description",
      headerKey: "adminMasterData.colDescription",
      render: (r) => r.description ?? DASH,
      className: "text-muted-foreground",
      sortValue: (r) => r.description,
    },
    {
      key: "rank",
      headerKey: "adminMasterData.colRank",
      render: (r) => r.rank,
      sortValue: (r) => r.rank,
    },
    {
      key: "responsibleUserId",
      headerKey: "adminMasterData.colResponsible",
      render: (r) =>
        r.responsibleUserId
          ? userMap.get(r.responsibleUserId)?.displayName ?? DASH
          : DASH,
      sortValue: (r) =>
        r.responsibleUserId
          ? userMap.get(r.responsibleUserId)?.displayName ?? null
          : null,
    },
  ];
  const areaFields: FieldSpec[] = [
    { name: "name", labelKey: "adminMasterData.colName", type: "text" },
    {
      name: "description",
      labelKey: "adminMasterData.colDescription",
      type: "textarea",
    },
    { name: "rank", labelKey: "adminMasterData.colRank", type: "number" },
    {
      name: "responsibleUserId",
      labelKey: "adminMasterData.colResponsible",
      type: "select",
      options: userOptions,
    },
  ];

  /* ----- Schweregrade ----- */
  const severityColumns: Column<AdminSeverity>[] = [
    {
      key: "name",
      headerKey: "adminMasterData.colName",
      render: (r) => r.name,
      sortValue: (r) => r.name,
    },
    {
      key: "description",
      headerKey: "adminMasterData.colDescription",
      render: (r) => r.description ?? DASH,
      className: "text-muted-foreground",
      sortValue: (r) => r.description,
    },
    {
      key: "rank",
      headerKey: "adminMasterData.colRank",
      render: (r) => r.rank,
      sortValue: (r) => r.rank,
    },
    {
      key: "recommendedReactionHours",
      headerKey: "adminMasterData.colReactionHours",
      render: (r) =>
        r.recommendedReactionHours == null
          ? DASH
          : t("adminMasterData.hours", { hours: r.recommendedReactionHours }),
      sortValue: (r) => r.recommendedReactionHours ?? null,
    },
    {
      key: "colorHint",
      headerKey: "adminMasterData.colColor",
      render: (r) =>
        r.colorHint ? (
          <span className="inline-flex items-center gap-2">
            <span
              aria-hidden="true"
              className="inline-block h-3 w-3 rounded-full ring-1 ring-black/10"
              style={{ backgroundColor: r.colorHint }}
            />
            {r.colorHint}
          </span>
        ) : (
          DASH
        ),
    },
  ];
  const severityFields: FieldSpec[] = [
    { name: "name", labelKey: "adminMasterData.colName", type: "text" },
    {
      name: "description",
      labelKey: "adminMasterData.colDescription",
      type: "textarea",
    },
    { name: "rank", labelKey: "adminMasterData.colRank", type: "number" },
    {
      name: "recommendedReactionHours",
      labelKey: "adminMasterData.colReactionHours",
      type: "number",
    },
    { name: "colorHint", labelKey: "adminMasterData.colColor", type: "color" },
  ];

  /* ----- Tags (DO-011 read-only stats) ----- */
  const tagColumns: Column<AdminTag>[] = [
    {
      key: "name",
      headerKey: "adminMasterData.colName",
      render: (r) => r.name,
      sortValue: (r) => r.name,
    },
    {
      key: "description",
      headerKey: "adminMasterData.colDescription",
      render: (r) => r.description ?? DASH,
      className: "text-muted-foreground",
      sortValue: (r) => r.description,
    },
    {
      key: "createdAt",
      headerKey: "adminMasterData.colCreatedAt",
      render: (r) => formatDate(r.createdAt),
      sortValue: (r) => r.createdAt,
      descFirst: true,
    },
    {
      key: "lastUsedAt",
      headerKey: "adminMasterData.colLastUsedAt",
      render: (r) => (r.lastUsedAt ? formatDate(r.lastUsedAt) : DASH),
      sortValue: (r) => r.lastUsedAt ?? null,
      descFirst: true,
    },
    {
      key: "usageCount",
      headerKey: "adminMasterData.colUsageCount",
      render: (r) => r.usageCount,
      sortValue: (r) => r.usageCount,
      descFirst: true,
    },
  ];
  const tagFields: FieldSpec[] = [
    { name: "name", labelKey: "adminMasterData.colName", type: "text" },
    {
      name: "description",
      labelKey: "adminMasterData.colDescription",
      type: "textarea",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("adminMasterData.title")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t("adminMasterData.subtitle")}
        </p>
        <p className="text-sm text-muted-foreground">
          {t("adminMasterData.auditHint")}
        </p>
      </div>

      <Tabs defaultValue="categories">
        <TabsList className="flex-wrap">
          <TabsTrigger value="categories">
            {t("adminMasterData.tabCategories")}
          </TabsTrigger>
          <TabsTrigger value="areas">
            {t("adminMasterData.tabAreas")}
          </TabsTrigger>
          <TabsTrigger value="severities">
            {t("adminMasterData.tabSeverities")}
          </TabsTrigger>
          <TabsTrigger value="tags">{t("adminMasterData.tabTags")}</TabsTrigger>
          {/* GR-008 "Pflegeverdaechtige Tags" - tags unused for 12 months. */}
          <TabsTrigger value="stale-tags" data-testid="admin-master-data_tab-stale-tags">
            {t("adminMasterData.tabStaleTags")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="categories">
          <MasterDataSection
            kind="categories"
            query={categoriesQuery}
            columns={categoryColumns}
            fields={categoryFields}
            toFormValues={(r) => ({
              name: r.name,
              description: s(r.description),
              rank: s(r.rank),
              symbolHint: s(r.symbolHint),
              parentId: s(r.parentId),
            })}
            buildBody={buildCategoryBody}
          />
        </TabsContent>

        <TabsContent value="areas">
          <MasterDataSection
            kind="areas"
            query={areasQuery}
            columns={areaColumns}
            fields={areaFields}
            toFormValues={(r) => ({
              name: r.name,
              description: s(r.description),
              rank: s(r.rank),
              responsibleUserId: s(r.responsibleUserId),
            })}
            buildBody={buildAreaBody}
          />
        </TabsContent>

        <TabsContent value="severities">
          <MasterDataSection
            kind="severities"
            query={severitiesQuery}
            columns={severityColumns}
            fields={severityFields}
            toFormValues={(r) => ({
              name: r.name,
              description: s(r.description),
              rank: s(r.rank),
              recommendedReactionHours: s(r.recommendedReactionHours),
              colorHint: s(r.colorHint),
            })}
            buildBody={buildSeverityBody}
          />
        </TabsContent>

        <TabsContent value="tags">
          <MasterDataSection
            kind="tags"
            query={tagsQuery}
            columns={tagColumns}
            fields={tagFields}
            toFormValues={(r) => ({
              name: r.name,
              description: s(r.description),
            })}
            buildBody={buildTagBody}
          />
        </TabsContent>

        <TabsContent value="stale-tags">
          <StaleTagsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
